import React, { useState } from "react";
import { Question } from "../../endpoints/results.type";
import { TimeSeriesDataPoint } from "src/redux/admin/reporting/reporting.types";
import MetricStackedBarChart from "src/pages/reports/components/metric-stacked-bar-chart/metric-stacked-bar-chart";
import { BarChart, LineChart } from "lucide-react";
import MetricChart from "src/pages/reports/components/metric-chart";
import QuestionAnswerButton from "./question-answer-button";

interface QuestionCardProps {
  index: number;
  question: Question;
  selectedDate: string;
  selectedInterval: number;
  selectedSurveyAnswers: string[];
  isLoading: boolean;
  onAnswerClick: (answerId: string) => void;
}

const colors = ['bg-yellow-500', 'bg-blue-500', 'bg-green-501', 'bg-red-502', 'bg-purple-500', 'bg-orange-500', 'bg-black', 'bg-gray-600', 'bg-green-1000' ];
const chartColors = ['#F9AB00', '#3257FB', '#1e8e3c', '#d93026', '#934DFF', '#F25A18', 'black', 'gray', 'green' ];

const QuestionCard: React.FC<QuestionCardProps> = ({ index, question, selectedDate, selectedInterval, selectedSurveyAnswers, isLoading, onAnswerClick }) => {

  const [chartType, setChartType] = useState<'bar' | 'line'>('line');

  // Get all unique dates from all answer intervals
  const allDates = new Set<string>();
  Object.values(question.answers).forEach(answer => {
    answer.intervals.forEach(interval => {
      allDates.add(interval.interval_start);
    });
  });

  // Create data points grouped by date, with each answer's raw_percent as a property
  const chartData = Array.from(allDates).map(date => {
    const dataPoint: any = { date };

    // For each answer, find the interval for this date and add its raw_percent (converted to 0-100 scale)
    Object.values(question.answers).forEach(answer => {
      const interval = answer.intervals.find(interval => interval.interval_start === date);
      if (interval) {
        dataPoint[answer.id] = interval.raw_percent * 100;
      }
    });

    return dataPoint as TimeSeriesDataPoint;
  })
    // Filter out data points where all raw_percent values are zero
    .filter(dataPoint => {
      const answerIds = Object.values(question.answers).map(answer => answer.id);
      const hasNonZeroValue = answerIds.some(answerId => {
        const value = dataPoint[answerId];
        return value !== undefined && value !== null && value !== 0;
      });
      return hasNonZeroValue;
    })
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  // Create metrics array with answer IDs as keys
  const metrics = Object.values(question.answers).map((answer, index) => ({
    key: answer.id,
    name: answer.title,
    color: chartColors[index % chartColors.length],
  }));

  return (
    <div className="rounded-lg shadow justify-between bg-white mb-4 overflow-hidden">
      <div className="flex flex-row items-stretch justify-between">
        <div className="flex flex-row">
          {Object.values(question.answers).map((answer, index) => (
            <QuestionAnswerButton
              key={answer.id}
              answer={answer}
              label={answer.title}
              color={colors[index % chartColors.length]}
              selected={selectedSurveyAnswers.length == 0 ? true : selectedSurveyAnswers.includes(answer.id) ? true : false}
              onClick={() => onAnswerClick(answer.id)}
            />
          ))}
        </div>
        <div className="flex items-stretch">
          <button
            type="button"
            className={`flex flex-col items-center justify-center text-gray-500 hover:opacity-65 disabled:opacity-50 px-4 border-b border-l border-gray-201 ${chartType === 'bar' ? 'bg-gray-201' : 'bg-white'}`}
            onClick={() => setChartType('bar')}
          >
            <BarChart className={`w-6 h-6 ${chartType === 'bar' ? 'text-black' : 'text-gray-500'}`} />
            <span className="text-xs font-light">
              Bar
            </span>
          </button>
          <button
            type="button"
            className={`flex flex-col items-center justify-center text-gray-500 hover:opacity-65 disabled:opacity-50 px-4 border-l border-b border-gray-201 ${chartType === 'line' ? 'bg-gray-201' : 'bg-white'}`}
            onClick={() => setChartType('line')}
          >
            <LineChart className={`w-6 h-6 ${chartType === 'line' ? 'text-black' : 'text-gray-500'}`} />
            <span className="text-xs font-light">
              Line
            </span>
          </button>
          {/* <Button
            variant="ghost"
            size="icon"
            className="bg-gray-201 text-black rounded-full hover:opacity-65"
            onClick={() => setChartType('bar')}
          >
            <BarChart className={`w-4 h-4 ${chartType === 'bar' ? 'text-black' : 'text-gray-500'}`} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="bg-gray-201 text-black rounded-full hover:opacity-65"
            onClick={() => setChartType('line')}
          >
            <LineChart className={`w-4 h-4 ${chartType === 'line' ? 'text-black' : 'text-gray-500'}`} />
          </Button> */}
        </div>
      </div>

      <div className="flex flex-row justify-between items-center p-4 pb-0">
        {/* <div className="flex items-center">
          <div className="flex size-6 shrink-0 rounded-full bg-gray-500 items-center justify-center">
            <div className="text-white">
              {index + 1}
            </div>
          </div>
          <h4 className="text-2xl font-light text-gray-600 ml-4">
            {question.title}
          </h4>
        </div> */}
        {/* <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="bg-gray-201 text-black rounded-full hover:opacity-65"
            onClick={() => setChartType('bar')}
          >
            <BarChart className={`w-4 h-4 ${chartType === 'bar' ? 'text-black' : 'text-gray-500'}`} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="bg-gray-201 text-black rounded-full hover:opacity-65"
            onClick={() => setChartType('line')}
          >
            <LineChart className={`w-4 h-4 ${chartType === 'line' ? 'text-black' : 'text-gray-500'}`} />
          </Button>
        </div> */}

      </div>
      {chartType === 'bar' ? (
        <MetricStackedBarChart
          className='border-none !p-2 !pb-4'
          data={chartData}
          metrics={metrics}
          isLoading={false}
          isNormalized={false}
          sharedYAxisDomain={true}
        />
      ) : (
        <MetricChart
          className='border-none !p-2 !pb-4'
          data={chartData}
          metrics={metrics}
          isLoading={false}
          isNormalized={false}
          sharedYAxisDomain={true}
        />
      )}
    </div>
  );
};

export default QuestionCard;